import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { pendenciaService } from '../../dominios/pendencias/services/pendencia'
import { PendenciaDto } from '../../dominios/pendencias/tipos/pendencia'
import { useToast } from '../../contexts/NotificationContext'
import { useMutationWithToast } from '../../hooks/useMutationWithToast'

interface ListaPendenciasProps {
  onClose: () => void
  onPendenciaMarcadaLida?: () => void
}

export function ListaPendencias({ onClose, onPendenciaMarcadaLida }: ListaPendenciasProps) {
  const navigate = useNavigate()
  const toast = useToast()
  const [pendencias, setPendencias] = useState<PendenciaDto[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [somenteNaoLidas, setSomenteNaoLidas] = useState(true)

  const carregarPendencias = async () => {
    try {
      setLoading(true)
      const dados = await pendenciaService.obterPendencias(somenteNaoLidas, page, 20)
      setPendencias(dados)
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao carregar pendências')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    carregarPendencias()
  }, [page, somenteNaoLidas])

  const marcarLidaMutation = useMutationWithToast({
    mutationFn: async (id: number) => {
      await pendenciaService.marcarComoLida(id)
    },
    successMessage: 'Pendência marcada como lida',
    errorMessage: 'Erro ao marcar pendência como lida',
    onSuccess: () => {
      carregarPendencias()
      onPendenciaMarcadaLida?.()
    }
  })

  const handleClickPendencia = async (pendencia: PendenciaDto) => {
    if (!pendencia.lida) {
      await marcarLidaMutation.mutateAsync(pendencia.idPendencia)
    }

    if (pendencia.rotaDestino) {
      navigate(pendencia.rotaDestino)
    } else {
      navigate(`/pendencias/${pendencia.idPendencia}`)
    }
    onClose()
  }

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Pendências</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 border-b">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={somenteNaoLidas}
              onChange={(e) => {
                setSomenteNaoLidas(e.target.checked)
                setPage(1)
              }}
              className="w-4 h-4 text-brand-500"
            />
            <span className="text-sm text-gray-700">Mostrar apenas não lidas</span>
          </label>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Carregando...</div>
          ) : pendencias.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Nenhuma pendência encontrada</div>
          ) : (
            <div className="space-y-2">
              {pendencias.map((pendencia) => (
                <div
                  key={pendencia.idPendencia}
                  onClick={() => handleClickPendencia(pendencia)}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    pendencia.lida
                      ? 'bg-gray-50 border-gray-200'
                      : 'bg-white border-brand-200 hover:border-brand-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={`font-medium ${pendencia.lida ? 'text-gray-600' : 'text-gray-900'}`}>
                          {pendencia.titulo}
                        </h3>
                        {!pendencia.lida && (
                          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        )}
                      </div>
                      {pendencia.descricao && (
                        <p className="text-sm text-gray-600 mb-2">{pendencia.descricao}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{formatarData(pendencia.dataCriacao)}</span>
                        <span className="px-2 py-0.5 bg-gray-100 rounded">{pendencia.tipo}</span>
                        {pendencia.usuarioCriadorNome && (
                          <span>Por: {pendencia.usuarioCriadorNome}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {pendencias.length > 0 && (
          <div className="p-4 border-t flex items-center justify-between">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
            >
              Anterior
            </button>
            <span className="text-sm text-gray-600">Página {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={pendencias.length < 20}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

